import { Component, OnInit, HostListener, ViewChild, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { FormsService, FormsSharedData } from './forms.service';
import { ApplicationDTO, ApplicationFormType, QuestionGroup, Question, Answer, FormDetailsDialogType, ConfirmationDialogType, ActionNoticeType } from './forms.model';
import { QuestionType } from 'src/AngularJS/Models/Quality/questions.models';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { GridOptions, RowNode, ICellRendererParams, _ } from 'ag-grid-community';
import { formatDate } from '@angular/common';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'app-forms',
    templateUrl: './forms.component.html',
    styleUrls: ['./forms.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class FormsComponent implements OnInit {
    @ViewChild('formsComponentGrid') agGrid: AgGridAngular;
    @ViewChild('formsComponentGlobalGrid') agGridGlobal: AgGridAngular;

    public isDataLoading = false;
    public isGlobalDataLoading = false;
    public isDataSaving = false;

    private readonly LOCALIZATION_SECTION = "Forms";
    private readonly LOCALIZATION_MESSAGES_SECTION = "Forms_Messages";
    private readonly LOCALIZATION_MESSAGES_SURVEY = "Survey_Forms";

    public fs: FormsSharedData;
    public surveyFolders: string[];
    public coachingFolders: string[];
    public qaFolders: string[];
    public permissions = {
        surveyCreate: false,
        coachingCreate: false,
        qaCreate: false,
        surveyWrite: false,
        coachingWrite: false,
        qaWrite: false,
        surveyRead: false,
        coachingRead: false,
        qaRead: false,
    };
    public globalGroups: QuestionGroup[];
    public cvAngularDateFormat: string = window["CVAngularDateFormat"];
    public cvAngularDateTimeFormat: string = window["CVAngularDateTimeFormat"];
    public localization: Map<string, string>;
    public localizationMessages: Map<string, string>;
    public localizationSurvey: Map<string, string>;
    public selectedFolder: string = "";
    public filterValue: string = "";
    public tab = ApplicationFormType.Survey;
    public gridOptions: GridOptions = null;
    public gridOptionsGlobal: GridOptions = null;

    public editMode = false;
    public formDetailsDialogTypeOpen = FormDetailsDialogType.None;
    public formDetailsEditing: ApplicationDTO;
    public showScrollButton = false;
    public surveyCollapsed = true;

    public assignDialogOpen = false;
    public globalDialogOpen = false;

    public confirmDialogTypeOpen = ConfirmationDialogType.None;
    public confirmParam1;
    public confirmParam2;
    public confirmParam3;
    public confirmParam4;

    public actionNoticeTypeOpen = ActionNoticeType.None;
    public actionNoticeName: string;
    public actionNoticeType: string;

    public selectedForm: ApplicationDTO = null;
    public selectedFormEditing: ApplicationDTO = null;
    public tempQuestionGroupId = -1;
    public tempQuestionId = -1;

    public selectedFormPrint: ApplicationDTO = null;
    public printAnswers: number[][] = [];

    public editSurveyEndDate: Date;
    public surveyIsIndefinite: boolean;

    get MultipleReadPermissions(): boolean {
        return +this.permissions.surveyRead + +this.permissions.coachingRead + +this.permissions.qaRead > 1;
    }

    get CurrentCreatePermission(): boolean {
        let applicationType = this.selectedForm == null ? this.tab : this.selectedForm.ApplicationType;

        switch (applicationType) {
            case ApplicationFormType.Survey:
                return this.permissions.surveyCreate;
            case ApplicationFormType.Coaching:
                return this.permissions.coachingCreate;
            case ApplicationFormType.QA:
                return this.permissions.qaCreate;
            default:
                return false;
        }
    }

    get CurrentWritePermission(): boolean {
        let applicationType = this.selectedForm == null ? this.tab : this.selectedForm.ApplicationType;

        switch (applicationType) {
            case ApplicationFormType.Survey:
                return this.permissions.surveyWrite;
            case ApplicationFormType.Coaching:
                return this.permissions.coachingWrite;
            case ApplicationFormType.QA:
                return this.permissions.qaWrite;
            default:
                return false;
        }
    }

    get CurrentReadPermission(): boolean {
        let applicationType = this.selectedForm == null ? this.tab : this.selectedForm.ApplicationType;

        switch (applicationType) {
            case ApplicationFormType.Survey:
                return this.permissions.surveyRead;
            case ApplicationFormType.Coaching:
                return this.permissions.coachingRead;
            case ApplicationFormType.QA:
                return this.permissions.qaRead;
            default:
                return false;
        }
    }

    get FirstCreatableFormType(): ApplicationFormType {
        if (this.permissions.surveyCreate)
            return ApplicationFormType.Survey;
        if (this.permissions.coachingCreate)
            return ApplicationFormType.Coaching;
        return ApplicationFormType.QA;
    }

    get FirstReadableFormType(): ApplicationFormType {
        if (this.permissions.surveyRead)
            return ApplicationFormType.Survey;
        if (this.permissions.coachingRead)
            return ApplicationFormType.Coaching;
        return ApplicationFormType.QA;
    }

    get FormHasBeenChanged(): boolean {
        // Remove QuestionTypes of -1, empty question groups and arbitrary properties before checking
        let temp = angular.copy(this.selectedFormEditing);
        this.cleanForm(temp, true);

        return !_.jsonEquals(this.selectedForm, temp);
    }

    constructor(
        private cvLocalizationFactory: CVLocalizationService,
        private formsService: FormsService,
        private agGridHelperService: AgGridHelperService,
        private cdr: ChangeDetectorRef,
    ) {
        this.fs = this.formsService.dataObj;
    }

    async ngOnInit() { // Bound values ("@Input()" above) are only valid in and after this function.
        this.localization = await this.cvLocalizationFactory.GetSection(this.LOCALIZATION_SECTION);
        this.localizationMessages = await this.cvLocalizationFactory.GetSection(this.LOCALIZATION_MESSAGES_SECTION);
        this.localizationSurvey = await this.cvLocalizationFactory.GetSection(this.LOCALIZATION_MESSAGES_SURVEY);
        this.setupAgGrids();
        this.loadForms();
    }

    /**
     * Sets up AG Grid objects.
     * */
    private setupAgGrids() {
        this.gridOptions = this.agGridHelperService.createGridOptions({
            columnDefs: [
                {
                    headerCheckboxSelection: true,
                    headerCheckboxSelectionFilteredOnly: true,
                    checkboxSelection: true,
                    field: "ApplicationName",
                    colId: "ApplicationName",
                    headerName: this.localization.get("Form_Name"),
                    onCellClicked: event => this.openFormEditingView(event.data),
                    cellClass: "color-cerulean clickable",
                },
                {
                    field: "Folder",
                    colId: "Folder",
                    headerName: this.localization.get("Folder"),
                },
                {
                    field: "ApplicationDescription",
                    colId: "ApplicationDescription",
                    headerName: this.localization.get("Form_Description"),
                },
                {
                    field: "DateCreated",
                    colId: "DateCreated",
                    headerName: this.localization.get("Date_Created"),
                    valueGetter: params => params.data[params.colDef.field] != null ? formatDate(params.data[params.colDef.field], this.cvAngularDateTimeFormat, "en-US") : null,
                    comparator: function (dateA, dateB, nodeA: RowNode, nodeB: RowNode, reverse: boolean) {
                        if (!nodeA.data.DateCreated && !nodeB.data.DateCreated)
                            return 0;
                        else if (!nodeA.data.DateCreated && nodeB.data.DateCreated)
                            return 1;
                        else if (nodeA.data.DateCreated && !nodeB.data.DateCreated)
                            return -1;
                        return new Date(nodeA.data.DateCreated).compareTo(new Date(nodeB.data.DateCreated));
                    },
                },
                {
                    field: "DateLastEdited",
                    colId: "DateLastEdited",
                    headerName: this.localization.get("Date_Last_Edited"),
                    valueGetter: params => params.data[params.colDef.field] != null ? formatDate(params.data[params.colDef.field], this.cvAngularDateTimeFormat, "en-US") : null,
                    comparator: function (dateA, dateB, nodeA: RowNode, nodeB: RowNode, reverse: boolean) {
                        if (!nodeA.data.DateLastEdited && !nodeB.data.DateLastEdited)
                            return 0;
                        else if (!nodeA.data.DateLastEdited && nodeB.data.DateLastEdited)
                            return 1;
                        else if (nodeA.data.DateLastEdited && !nodeB.data.DateLastEdited)
                            return -1;
                        return new Date(nodeA.data.DateLastEdited).compareTo(new Date(nodeB.data.DateLastEdited));
                    },
                }
            ],
            defaultColDef: {
                sortable: true,
                cellClass: "d-flex align-items-center",
                menuTabs: []
            },
            onGridReady: params => {
                if (this.fs.forms)
                    params.api.setGridOption('rowData', this.fs.forms);

                params.api.sizeColumnsToFit();
            },
            getRowId: ({ data: rowData }) => rowData.ApplicationID,
            isExternalFilterPresent: () => true,
            doesExternalFilterPass: node => this.agGridHelperService.doesExternalFilterPass(node, this.filterValue)
                && node.data.ApplicationType == this.tab && (this.selectedFolder == "" || this.selectedFolder == node.data.Folder),
            onGridSizeChanged: gridEvent => {
                if (gridEvent.clientWidth > 0)
                    gridEvent.api.sizeColumnsToFit();
            },
            onColumnVisible: event => event.api.sizeColumnsToFit(),
            suppressRowClickSelection: true,
            suppressCellFocus: true,
            suppressMovableColumns: true,
            rowHeight: 42
        });

        this.gridOptionsGlobal = this.agGridHelperService.createGridOptions({
            columnDefs: [
                {
                    headerCheckboxSelection: true,
                    headerCheckboxSelectionFilteredOnly: true,
                    checkboxSelection: true,
                    field: "QuestionGroupName",
                    colId: "QuestionGroupName",
                    headerName: this.localization.get("Section_Name"),
                },
                /*{
                    field: "MetricInUse",
                    colId: "MetricInUse",
                    //headerName: this.localization.get("Metric_In_Use"),
                    headerName: "Metric In Use",
                },*/
                // TODO: Implement... whatever this field is
                {
                    colId: "QuestionCount",
                    headerName: this.localization.get("Questions"),
                    valueGetter: (params) => this.groupQuestionCount(params.data),
                    cellRenderer: (params: ICellRendererParams) => { return this.groupQuestionCount(params.data).toString(); },
                },
                {
                    field: "QuestionGroupDescription",
                    colId: "QuestionGroupDescription",
                    headerName: this.localization.get("Description"),
                }
            ],
            defaultColDef: {
                sortable: true,
                cellClass: "d-flex align-items-center",
                menuTabs: []
            },
            onGridReady: params => {
                if (this.globalGroups)
                    params.api.setGridOption('rowData', this.globalGroups);
            },
            getRowId: ({ data: rowData }) => rowData.QuestionGroupID,
            onGridSizeChanged: gridEvent => {
                if (gridEvent.clientWidth > 0)
                    gridEvent.api.sizeColumnsToFit();
            },
            onColumnVisible: event => event.api.sizeColumnsToFit(),
            rowMultiSelectWithClick: true,
            suppressCellFocus: true,
            suppressMovableColumns: true,
            rowHeight: 42
        });
    }

    /**
     * Loads all forms for the current company
     * */
    private async loadForms(reload? : boolean) {
        if (this.isDataLoading)
            return;

        try {
            this.isDataLoading = true;
            let returnObj = await this.formsService.loadForms();
            this.fs.forms = returnObj.Forms;

            this.surveyFolders = returnObj.SurveyFolders;
            this.coachingFolders = returnObj.CoachingFolders;
            this.qaFolders = returnObj.QaFolders;

            this.permissions = {
                surveyCreate: returnObj.SurveyCreatePermission,
                coachingCreate: returnObj.CoachingCreatePermission,
                qaCreate: returnObj.QaCreatePermission,
                surveyWrite: returnObj.SurveyWritePermission,
                coachingWrite: returnObj.CoachingWritePermission,
                qaWrite: returnObj.QaWritePermission,
                surveyRead: returnObj.SurveyReadPermission,
                coachingRead: returnObj.CoachingReadPermission,
                qaRead: returnObj.QaReadPermission
            };
            if (!reload)
                this.tab = this.FirstReadableFormType;

            this.agGrid?.api.setGridOption('rowData', this.fs.forms);
            this.agGrid?.api.sizeColumnsToFit();
        }
        catch (error) {
            console.log(error);
        }
        finally {
            this.isDataLoading = false;
        }
    }

    /**
     * Loads the extended data for a form (question groups, questions, answers, assigned groups, and survey information) and inserts it
     * @param form Form to pull information and insert into
     */
    private async loadFormExtendedData(form: ApplicationDTO) {
        try {
            this.isDataLoading = true;
            let returnObj = await this.formsService.loadFormExtendedData(form.ApplicationID);
            form.QuestionGroups = returnObj.QuestionGroups;
            form.GroupsAssigned = returnObj.Groups;
            form.Survey = returnObj.Survey;

            if (form.Survey != null) {
                this.editSurveyEndDate = form.Survey.EndDate ?? new Date();
                this.surveyIsIndefinite = form.Survey.EndDate == null;
            }
        }
        catch (error) {
            console.log(error);
        }
        finally {
            this.isDataLoading = false;
        }
    }

    /**
     * Opens a form for editing, pulling its question groups, questions and answers if necessary.
     * @param form Form to open for editing
     */
    private async openFormEditingView(form: ApplicationDTO) {
        if (!form.QuestionGroups)
            await this.loadFormExtendedData(form);

        this.selectedForm = form;
        this.revertChanges();
    }

    /**
     * Opens a form for printing, pulling its question groups, questions and answers if necessary.
     * @param form Form to open for printing
     */
    public async openFormPrintView(form: ApplicationDTO) {
        if (!form.QuestionGroups)
            await this.loadFormExtendedData(form);

        // Construct answer array
        this.printAnswers = [];
        for (let group of form.QuestionGroups) {
            let groupArray: number[] = [];
            for (let _question of group.Questions) {
                groupArray.push(0);
            }
            this.printAnswers.push(groupArray);
        }

        this.selectedFormPrint = form;
    }

    /**
     * Loads all global question groups. Will do nothing if the groups have been or currently being loaded or if the user has no write permissions.
     * */
    public loadGlobalGroups() {
        if (this.globalGroups || this.isGlobalDataLoading || (!this.permissions.surveyWrite && !this.permissions.coachingWrite && !this.permissions.qaWrite))
            return;

        this.isGlobalDataLoading = true;
        this.formsService.loadGlobalGroups().then((returnObj) => {
            this.globalGroups = returnObj;

            this.agGridGlobal.api.setGridOption('rowData', this.globalGroups);
            if (this.globalDialogOpen)
                this.agGridGlobal.api.sizeColumnsToFit();
        }).catch((error) => {
                console.log(error);
        }).finally(() => {
            this.isGlobalDataLoading = false;
        });
    }

    /**
     * Initiates process to leave editing view and return to landing page.
     * */
    public async leaveEditing() {
        if (this.FormHasBeenChanged)
            this.confirmDialogTypeOpen = ConfirmationDialogType.LeaveWithoutSaving;
        else
            this.selectedForm = null;
        await this.loadForms(true);
    }

    /**
     * Reverts any unsaved changes to a form.
     * */
    public revertChanges() {
        this.editMode = false;
        this.selectedFormEditing = angular.copy(this.selectedForm);
        this.tempQuestionGroupId = -1;
        this.tempQuestionId = -1;
        this.populatePointDisplay(this.selectedFormEditing);
        this.surveyIsIndefinite = this.selectedForm.Survey.EndDate == null;

        this.confirmDialogTypeOpen = ConfirmationDialogType.None;
    }

    /**
     * If the input string is 28 characters or less, then the input string will be passed back. If it is longer, then the first 25 characters plus "..." will be returned.
     * @param string
     */
    public truncateString(string: string) {
        if (!string || string.length <= 28)
            return string;

        return string.substr(0, 25) + "...";
    }

    /**
     * Returns comma-separated list of current group assignment names for the selected form.
     * @param form Form to get assignment names of
     */
    public assignmentNames(form?: ApplicationDTO) {
        let formToUse = !!form ? form : this.selectedFormEditing;

        let names = "";

        if (!!formToUse.GroupsAssigned)
            for (let group of formToUse.GroupsAssigned) {
                names += group.GroupName + ", ";
            }

        return names.slice(0, names.length - 2);
    }

    /**
     * Returns the total number of active questions in a form. Only works if the questions and question groups have already been pulled.
     * @param form Form to get question count of
     */
    public formQuestionCount(form: ApplicationDTO) {
        if (!form || !form.QuestionGroups || form.QuestionGroups.length == 0)
            return 0;

        let total = 0;

        for (let group of form.QuestionGroups) {
            total += this.groupQuestionCount(group);
        }

        return total;
    }

    /**
     * Returns the total number of active questions in a group.
     * @param group Group to get question count of
     */
    public groupQuestionCount(group: QuestionGroup) {
        if (!group)
            return 0;

        let total = 0;

        for (let question of group.Questions) {
            if (question.Active)
                total++;
        }

        return total;
    }

    /**
     * Returns the total number of active answers in a question.
     * @param question Question to get answer count of
     */
    public questionAnswerCount(question: Question) {
        if (!question)
            return 0;

        let total = 0;

        for (let answer of question.Answers) {
            if (answer.Active)
                total++;
        }

        return total;
    }

    /**
     * Opens the form details modal in 
     * @param mode 1: New form; 2: Edit form; 3: Copy form
     * @param form Required for edit and copy
     */
    public async openFormDetails(mode: FormDetailsDialogType, form?: ApplicationDTO) {
        switch (mode) {
            case FormDetailsDialogType.New:
                this.formDetailsEditing = <ApplicationDTO>{
                    ApplicationID: 0,
                    CompanyID: 0,
                    ApplicationName: "",
                    ApplicationDescription: "",
                    RecordingType: null,
                    Active: true,
                    ApplicationType: this.tab,
                    ReadAccess: false,
                    showInGrid: true,
                    isSelected: false,
                    QuestionGroups: [],
                    showDescriptionInput: false,
                    Folder: "",
                    DateCreated: null,
                    DateLastEdited: null,
                    GroupsAssigned: [],
                    Survey: null
                };
                break;
            case FormDetailsDialogType.Edit:
                this.formDetailsEditing = angular.copy(form);
                break;
            case FormDetailsDialogType.Copy:
                if (!form.QuestionGroups)
                    await this.loadFormExtendedData(form);
                this.formDetailsEditing = angular.copy(form);
                this.formDetailsEditing.ApplicationType = this.CurrentCreatePermission ? this.tab : this.FirstCreatableFormType;
                break;
        }
        this.formDetailsDialogTypeOpen = mode;
    }

    /**
     * Generates a new form based on user input and pushes it to be saved to the database.
     * */
    public async createNewForm() {
        if (this.isDataSaving)
            return;

        let returnObj;
        try {
            this.isDataSaving = true;
            returnObj = await this.formsService.addForm(this.formDetailsEditing);
        }
        catch (err) {
            console.log(err);
            return;
        }
        finally {
            this.isDataSaving = false;
        }

        // Handle returned data
        this.selectedForm = returnObj.Form;
        this.selectedForm.QuestionGroups = [];
        this.selectedForm.GroupsAssigned = [];
        this.selectedForm.Survey = returnObj.Survey ?? angular.copy(this.formsService.emptySurvey);

        if (this.selectedForm.ApplicationType == ApplicationFormType.Survey) {
            this.surveyIsIndefinite = this.selectedForm.Survey.EndDate == null;
            this.editSurveyEndDate = this.selectedForm.Survey.EndDate ?? new Date();
        }

        this.fs.forms.push(this.selectedForm);
        this.agGrid?.api.setGridOption('rowData', this.fs.forms);

        let folderArray;
        switch (this.selectedForm.ApplicationType) {
            case ApplicationFormType.Survey:
                folderArray = this.surveyFolders;
                break;
            case ApplicationFormType.Coaching:
                folderArray = this.coachingFolders;
                break;
            case ApplicationFormType.QA:
                folderArray = this.qaFolders;
                break;
        }
        if (!folderArray.includes(this.selectedForm.Folder))
            folderArray.push(this.selectedForm.Folder);

        this.revertChanges();
        this.addQuestionGroup();

        this.formDetailsDialogTypeOpen = FormDetailsDialogType.None;

        this.actionNoticeTypeOpen = ActionNoticeType.Added;
        this.actionNoticeName = this.selectedForm.ApplicationName;
        switch (this.selectedForm.ApplicationType) {
            case ApplicationFormType.Survey:
                this.actionNoticeType = "Survey/Quiz";
                break;
            case ApplicationFormType.Coaching:
                this.actionNoticeType = "Coaching";
                break;
            case ApplicationFormType.QA:
                this.actionNoticeType = "Quality Assurance";
                break;
        }
        this.editMode = true;
    }

    /**
     * Saves inputted details on the form.
     * */
    public async saveFormDetails() {
        if (this.isDataSaving)
            return;

        try {
            this.isDataSaving = true;
            await this.formsService.editForm(this.formDetailsEditing);
        }
        catch (err) {
            console.log(err);
            return;
        }
        finally {
            this.isDataSaving = false;
        }

        // Update data now that it's been saved
        if (this.selectedForm != null) {
            this.selectedForm.ApplicationName = this.formDetailsEditing.ApplicationName;
            this.selectedForm.ApplicationType = this.formDetailsEditing.ApplicationType;
            this.selectedForm.Folder = this.formDetailsEditing.Folder;
            this.selectedForm.ApplicationDescription = this.formDetailsEditing.ApplicationDescription;
            // Technically, this is wrong and not based on the value in the database. But getting an updated value after an edit is a bit roundabout, and this should be accurate enough until a page reload.
            this.selectedForm.DateLastEdited = new Date();

            this.selectedFormEditing.ApplicationName = this.formDetailsEditing.ApplicationName;
            this.selectedFormEditing.ApplicationType = this.formDetailsEditing.ApplicationType;
            this.selectedFormEditing.Folder = this.formDetailsEditing.Folder;
            this.selectedFormEditing.ApplicationDescription = this.formDetailsEditing.ApplicationDescription;
        }
        else {
            for (let i = 0; i < this.fs.forms.length; i++) {
                if (this.formDetailsEditing.ApplicationID == this.fs.forms[i].ApplicationID) {
                    this.fs.forms.splice(i, 1, this.formDetailsEditing);
                    break;
                }
            }
        }
        this.agGrid?.api.setGridOption('rowData', this.fs.forms);

        let folderArray;
        switch (this.formDetailsEditing.ApplicationType) {
            case ApplicationFormType.Survey:
                folderArray = this.surveyFolders;
                break;
            case ApplicationFormType.Coaching:
                folderArray = this.coachingFolders;
                break;
            case ApplicationFormType.QA:
                folderArray = this.qaFolders;
                break;
        }
        if (!folderArray.includes(this.formDetailsEditing.Folder))
            folderArray.push(this.formDetailsEditing.Folder);

        this.formDetailsDialogTypeOpen = FormDetailsDialogType.None;
    }

    /**
     * Copies a specified form with the noted changes.
     * */
    public async copyForm() {
        if (this.isDataSaving)
            return;

        let returnObj;
        try {
            this.isDataSaving = true;
            returnObj = await this.formsService.copyForm(this.formDetailsEditing);
        }
        catch (err) {
            console.log(err);
            return;
        }
        finally {
            this.isDataSaving = false;
        }

        this.selectedForm = returnObj;

        this.fs.forms.push(this.selectedForm);
        this.agGrid?.api.setGridOption('rowData', this.fs.forms);

        let folderArray;
        switch (this.selectedForm.ApplicationType) {
            case ApplicationFormType.Survey:
                folderArray = this.surveyFolders;
                break;
            case ApplicationFormType.Coaching:
                folderArray = this.coachingFolders;
                break;
            case ApplicationFormType.QA:
                folderArray = this.qaFolders;
                break;
        }
        if (!folderArray.includes(this.selectedForm.Folder))
            folderArray.push(this.selectedForm.Folder);

        this.revertChanges();

        this.formDetailsDialogTypeOpen = FormDetailsDialogType.None;

        this.actionNoticeTypeOpen = ActionNoticeType.Copied;
        this.actionNoticeName = this.selectedForm.ApplicationName;
        switch (this.selectedForm.ApplicationType) {
            case ApplicationFormType.Survey:
                this.actionNoticeType = "Survey/Quiz";
                break;
            case ApplicationFormType.Coaching:
                this.actionNoticeType = "Coaching";
                break;
            case ApplicationFormType.QA:
                this.actionNoticeType = "Quality Assurance";
                break;
        }
    }

    /**
     * Deletes all forms currently selected in the AG Grid.
     * */
    public async deleteForms() {
        if (this.isDataSaving)
            return;

        let formsToBeDeleted = this.agGrid?.api.getSelectedRows();

        if (formsToBeDeleted.length < 1)
            return;

        try {
            this.isDataSaving = true;
            await this.formsService.deleteForms(formsToBeDeleted);
        }
        catch (err) {
            console.log(err);
            return;
        }
        finally {
            this.isDataSaving = false;
        }

        for (let form of formsToBeDeleted) {
            this.fs.forms = this.fs.forms.filter(o => o !== form);
        }
        this.agGrid?.api.setGridOption('rowData', this.fs.forms);

        this.confirmDialogTypeOpen = ConfirmationDialogType.None;

        this.actionNoticeTypeOpen = formsToBeDeleted.length == 1 ? ActionNoticeType.Deleted : ActionNoticeType.MultiDeleted;
        this.actionNoticeName = formsToBeDeleted[0].ApplicationName;
    }

    /**
     * Saves the edited form.
     * @param fromConfirmation: If true, returns to landing page
     * */
    public async saveFormExtendedData(fromConfirmation?: boolean) {
        if (this.isDataSaving)
            return;

        // Remove any questions of type -1
        this.cleanForm(this.selectedFormEditing);

        // Detect errors in inputted data, stop save and prompt if found
        for (let group of this.selectedFormEditing.QuestionGroups) {
            if (!group.QuestionGroupName && group.Active) {
                this.confirmDialogTypeOpen = ConfirmationDialogType.EmptyNameField;
                return;
            }

            for (let question of group.Questions) {
                if (!question.QuestionName && question.Active) {
                    this.confirmDialogTypeOpen = ConfirmationDialogType.EmptyNameField;
                    return;
                }

                let checkDependents = question.HasDependents;
                let triggers = 0;

                for (let answer of question.Answers) {
                    if (!answer.AnswerName && answer.Active) {
                        this.confirmDialogTypeOpen = ConfirmationDialogType.EmptyNameField;
                        return;
                    }

                    if (checkDependents && answer.ShowsDependents)
                        triggers++;
                }

                if (checkDependents && triggers == 0) {
                    this.confirmDialogTypeOpen = ConfirmationDialogType.UnshowableDependents;
                    return;
                }
            }
        }

        let submission = angular.copy(this.selectedFormEditing);
        if (submission.Survey.StartDate === undefined)
            submission.Survey = null;

        this.isDataSaving = true;
        let editedForm = null;
        // Save form, then load extended data for it
        try {
            editedForm = await this.formsService.editFormExtendedData(submission);
            await this.loadFormExtendedData(editedForm);
        }
        catch (err) {
            console.log(err);
            return;
        }
        finally {
            this.isDataSaving = false;
        }
        // Replace existing form objects with one that was just returned from the API
        this.selectedForm = editedForm;

        for (let i = 0; i < this.fs.forms.length; i++) {
            if (this.selectedForm.ApplicationID == this.fs.forms[i].ApplicationID) {
                this.fs.forms.splice(i, 1, this.selectedForm);
                break;
            }
        }

        this.revertChanges();

        this.globalGroups = null;

        if (fromConfirmation)
            this.selectedForm = null;
    }

    /**
     * Removes all questions from the form that have a QuestionType of -1 and all sections that are empty or only had questions of type -1.
     * @param form Form to clean
     * @param resetArbitrary If true, deletes all questions' arbitrary properties (like questionCollapsed). If false, property is not touched. Useful for change detection.
     */
    public cleanForm(form: ApplicationDTO, resetArbitrary = false) {
        if (!!form.QuestionGroups && form.QuestionGroups.length > 0) {
            // Remove temp questions
            for (let i = 0; i < form.QuestionGroups.length; i++) {
                let group = form.QuestionGroups[i];

                if (!!group.Questions && group.Questions.length > 0) {
                    for (let j = 0; j < group.Questions.length; j++) {
                        if (group.Questions[j].QuestionType < 0) {
                            this.removeQuestion(j, group, true);
                            j--;
                            continue;
                        }

                        if (resetArbitrary && !!group.Questions[j].Answers && group.Questions[j].Answers.length > 0) {
                            for (let answer of group.Questions[j].Answers) {
                                delete answer.PercentCorrectDisplay;
                            }
                        }

                        if (resetArbitrary) {
                            delete group.Questions[j].questionCollapsed;
                            delete group.Questions[j].showDescriptionInput;
                        }
                    }
                }

                // Remove empty newly-added groups - existing groups will not be touched
                if (group.Questions.length == 0 && group.QuestionGroupID < 0) {
                    this.removeQuestionGroup(i, form, true);
                    i--;
                    continue;
                }

                if (resetArbitrary)
                    delete group.showDescriptionInput;
            }
        }
        if (form.ApplicationType == ApplicationFormType.Survey) {
            form.Survey.EndDate = this.surveyIsIndefinite ? null : this.editSurveyEndDate;
        }
    }

    /**
     * Adds a blank question group to the end of a form with one question that hasn't had a type assigned yet.
     * */
    public addQuestionGroup() {
        let newQuestionGroup: QuestionGroup = <QuestionGroup>{
            QuestionGroupID: this.tempQuestionGroupId--,
            QuestionGroupName: "",
            QuestionGroupDescription: "",
            CompanyID: 0,
            ApplicationID: this.selectedForm.ApplicationID,
            Active: true,
            isGlobal: false,
            Position: 0,
            showInGrid: true,
            isSelected: false,
            Questions: [],
            isUsed: true,
            showDescriptionInput: false,
            moreMenuOpen: false
        };
        this.addQuestion(newQuestionGroup, -1);
        this.selectedFormEditing.QuestionGroups.push(newQuestionGroup);

        this.refreshPositions();
    }

    /**
     * Adds all currently selected global groups to the end of the form.
     * */
    public addGlobalGroups() {
        let groupsToBeAdded = this.agGridGlobal.api.getSelectedRows();

        if (groupsToBeAdded.length < 1)
            return;

        for (let globalGroup of groupsToBeAdded) {
            let globalGroupCopy = angular.copy(globalGroup);
            if (globalGroupCopy.Questions.length < 1)
                this.addQuestion(globalGroupCopy, -1);

            this.selectedFormEditing.QuestionGroups.push(globalGroupCopy);
        }

        this.populatePointDisplay(this.selectedFormEditing);
        this.refreshPositions();
        this.globalDialogOpen = false;
        this.agGridGlobal.api.deselectAll();
    }

    /**
     * Sets the specified Question Group's isUsed value to false and closes the confirmation dialog.
     * @param questionGroup Question Group to deactivate.
     */
    public deactivateQuestionGroup(questionGroup: QuestionGroup) {
        questionGroup.isUsed = false;

        this.confirmDialogTypeOpen = ConfirmationDialogType.None;
    }

    /**
     * Removes a question group from the selected form.
     * @param questionGroupIndex Index of the group to be removed
     * @param form Form to remove question group from
     * @param fromClean Optional; true if being called from cleanForm() method
     */
    public removeQuestionGroup(questionGroupIndex: number, form: ApplicationDTO, fromClean = false) {
        let group = form.QuestionGroups[questionGroupIndex];

        if (group.QuestionGroupID < 0)
            form.QuestionGroups.splice(questionGroupIndex, 1);
        else
            group.Active = false;

        if (!fromClean)
            this.confirmDialogTypeOpen = ConfirmationDialogType.None;
    }

    /**
     * Moves a question group to a different position.
     * @param questionIndex Original index of the question group
     * @param newIndex New index of the question group
     */
    public changeQuestionGroupOrder(currentIndex: number, newIndex: number) {
        if (currentIndex == newIndex || newIndex < 0) {
            this.refreshPositions();
            return;
        }

        let group = this.selectedFormEditing.QuestionGroups.splice(currentIndex, 1)[0];
        this.selectedFormEditing.QuestionGroups.splice(newIndex, 0, group);

        this.refreshPositions();
    }

    /**
     * Adds a new question to a question group. Also auto-adds pre-made answers to the question depending on the type, if applicable.
     * @param questionGroup Question group to add the question to
     * @param questionType Type of the new question; this determines how many and what kind of answers are auto-added to it
     */
    public addQuestion(questionGroup: QuestionGroup, questionType: QuestionType, index?: number) {
        let nextQuestion;
        if (index != null && index <= questionGroup.Questions.length)
            nextQuestion = questionGroup.Questions[index];

        let newQuestion: Question = <Question>{
            QuestionID: this.tempQuestionId--,
            QuestionGroupID: questionGroup.QuestionGroupID,
            QuestionName: "",
            QuestionDescription: "",
            QuestionPath: "",
            QuestionType: questionType,
            QuestionWeight: 0,
            Active: true,
            Position: questionGroup.Questions.length + 1, // Dependent questions can throw off this number, hence the call for refreshPositions() below
            DisplayPosition: String(questionGroup.Questions.length + 1),
            DependentID: nextQuestion?.DependentID > 0 ? nextQuestion.DependentID : 0,
            HasDependents: false,
            showInGrid: true,
            isSelected: false,
            SelectedAnswerIndex: 0,
            SelectedAnswer: null,
            Answers: [],
            QuestionTypeTemp: 1,
            questionCollapsed: false,
            showDescriptionInput: false
        };

        this.addDefaultAnswers(newQuestion);

        if (index == null)
            questionGroup.Questions.push(newQuestion);
        else
            questionGroup.Questions.splice(index, 0, newQuestion);

        this.refreshPositions();

        return newQuestion;
    }

    /**
     * Adds a new question that is automatically set to being dependent on an existing one
     * @param questionGroup Question group that the parent question is a part of
     * @param parentQuestion Question that the new question will be dependent on and placed immediately after
     */
    public addDependentQuestion(questionGroup: QuestionGroup, parentQuestion: Question, parentIndex: number) {
        // Determine index to add question at
        let insertionIndex = parentIndex + 1;
        for (insertionIndex; insertionIndex < questionGroup.Questions.length; insertionIndex++) {
            if (questionGroup.Questions[insertionIndex]?.DependentID != parentQuestion.QuestionID)
                break;
        }

        let childQuestion = this.addQuestion(questionGroup, -1, insertionIndex);
        this.makeDependent(parentQuestion, childQuestion);
    }

    /**
     * Removes a question from a question group.
     * @param questionIndex Index of question to remove
     * @param questionGroup Question group to remove question from
     */
    public removeQuestion(questionIndex: number, questionGroup: QuestionGroup, fromClean = false) {
        let question = questionGroup.Questions[questionIndex];
        // Only run this if the question is the last in the dependency chain
        if (questionIndex + 1 == questionGroup.Questions.length || questionGroup.Questions[questionIndex + 1].DependentID != question.DependentID)
            this.removeDependency(question, questionIndex, questionGroup);

        if (question.QuestionID < 0)
            questionGroup.Questions.splice(questionIndex, 1);
        else
            question.Active = false;

        if (!fromClean) {
            this.refreshPositions();
            this.confirmDialogTypeOpen = ConfirmationDialogType.None;
        }
    }

    /**
     * Makes a child question dependent on a parent question.
     * @param parentQuestion Question whose answers will affect visibility of child
     * @param childQuestion Question whose visibility depends on the answers of its parent
     */
    public makeDependent(parentQuestion: Question, childQuestion: Question) {
        if (parentQuestion.QuestionType == QuestionType.Text)
            return;

        childQuestion.DependentID = parentQuestion.QuestionID;
        parentQuestion.HasDependents = true;

        for (let answer of parentQuestion.Answers) {
            if (answer.ShowsDependents)
                return;
        }
        parentQuestion.Answers[0].ShowsDependents = true;

        this.refreshPositions();
    }

    /**
     * If the question prior to the target ("P") is not part of a dependent chain, then the target question will be made into a dependent of P.
     * If P is a dependent of another question, then the target will become dependent on it.
     * @param targetQuestion Question to add to end of dependent chain
     * @param targetQuestionIndex Index of target question
     * @param questionGroup Group that the question belongs to
     */
    public addToDependentChain(targetQuestion: Question, targetQuestionIndex: number, questionGroup: QuestionGroup) {
        if (!this.questionCanBeDependent(targetQuestion, targetQuestionIndex, questionGroup))
            return;

        let previousQuestion = questionGroup.Questions[targetQuestionIndex - 1];

        if (previousQuestion.DependentID == 0)
            this.makeDependent(previousQuestion, targetQuestion);
        else {
            let parentQuestion = questionGroup.Questions.find(o => o.QuestionID == previousQuestion.DependentID);
            this.makeDependent(parentQuestion, targetQuestion);
        }
    }

    /**
     * Returns true if the target question can be (but is currently not) be added to the end of the preceeding dependent chain
     * @param targetQuestion Question to determine dependency availability
     * @param targetQuestionIndex Index of target question
     * @param questionGroup Group that the question belongs to
     */
    public questionCanBeDependent(targetQuestion: Question, targetQuestionIndex: number, questionGroup: QuestionGroup) {
        if (targetQuestionIndex == 0 || targetQuestion.HasDependents || targetQuestion.DependentID != 0)
            return false;

        let previousQuestion = questionGroup.Questions[targetQuestionIndex - 1];

        return previousQuestion.QuestionType != QuestionType.Text || previousQuestion.DependentID != 0;
    }

    /**
     * When called on a dependent child question, this makes it and all immediately following questions independent, and removes parent's status, if needed.
     * When called on a parent, this makes all of its dependent questions independent and removes the target question's parental status.
     * @param childQuestion Question to strip dependent status of
     * @param group Group that the question belongs to
     */
    public removeDependency(targetQuestion: Question, targetQuestionIndex: number, group: QuestionGroup) {
        if (!targetQuestion.HasDependents && targetQuestion.DependentID == 0)
            return;

        let previousQuestion = group.Questions[targetQuestionIndex - 1];

        if (targetQuestion.HasDependents) { // Target is a parent, remove dependency info for it and all children
            targetQuestion.HasDependents = false;
            for (let i = targetQuestionIndex + 1; i < group.Questions.length; i++) {
                if (group.Questions[i].DependentID == 0)
                    break;

                group.Questions[i].DependentID = 0;
            }
        }
        else if (previousQuestion.HasDependents) // Target is first child, functionally the same as running on the parent
            this.removeDependency(previousQuestion, targetQuestionIndex - 1, group);
        else { // Target is second or later child, remove dependency from it and following children
            for (let i = targetQuestionIndex; i < group.Questions.length; i++) {
                if (group.Questions[i].DependentID == 0)
                    break;

                group.Questions[i].DependentID = 0;
            }
        }

        this.refreshPositions();
    }

    /**
     * Moves a question to a different position within the same group.
     * @param questionIndex Original index of the question. This is NOT its "Position" value
     * @param questionGroup Group that the question is a part of
     * @param newIndex New index of the question
     */
    public changeQuestionOrder(previousIndex: number, questionGroup: QuestionGroup, newIndex: number) {
        if (previousIndex == newIndex || newIndex < 0) {
            this.refreshPositions();
            return;
        }

        let question = questionGroup.Questions[previousIndex];
        if (question.HasDependents || question.DependentID != 0)
            this.removeDependency(question, previousIndex, questionGroup);

        question = questionGroup.Questions.splice(previousIndex, 1)[0];
        questionGroup.Questions.splice(newIndex, 0, question);

        // Handle dependency
        if (newIndex < questionGroup.Questions.length - 1)
            question.DependentID = questionGroup.Questions[newIndex + 1].DependentID != 0 ? questionGroup.Questions[newIndex + 1].DependentID : 0;

        this.refreshPositions();
    }

    /**
     * Method called when something is dropped into a question group area.
     * @param event Drag-and-drop event
     * @param group Question group the question is moving around within
     */
    public dropQuestionGroup(event: CdkDragDrop<Question[]>, group: QuestionGroup) {
        if (event.previousContainer.id == "types-list") // Question type dropped from sidebar
            this.addQuestion(group, event.item.data, event.currentIndex);
        else if (event.container.id == event.previousContainer.id) // Question moved around within same group
            this.changeQuestionOrder(event.previousIndex, group, event.currentIndex);
        else { // Question moved to a different group
            // Place question at the end of the destination group, remove it from the previous group, then move it to where it should be in the destination group
            let previousGroup = this.selectedFormEditing.QuestionGroups[parseInt(event.previousContainer.id.substr(16))];
            let question = previousGroup.Questions[event.previousIndex];

            question.QuestionGroupID = group.QuestionGroupID;
            group.Questions.push(question);
            previousGroup.Questions.splice(event.previousIndex, 1);

            this.changeQuestionOrder(group.Questions.length - 1, group, event.currentIndex);
        }
    }

    /**
     * Used to prevent drag-and-drop lists from having things dropped into them.
     * */
    public noReturnPredicate() {
        return false;
    }

    /**
     * Refresh question position values.
     */
    public refreshPositions() {
        // TODO: Ensure that these are the actual rules for position values
        let groupCounter = 1;
        let questionCounter = 1;
        let questionOrderCounter = 1;
        let dependentCounter = 1;
        let answerCounter = 1;

        for (let group of this.selectedFormEditing.QuestionGroups) {
            if (!group.Active)
                continue;

            group.Position = groupCounter++;

            for (let question of group.Questions) {
                if (!question.Active)
                    continue;

                question.OrderPosition = questionOrderCounter++;

                if (question.DependentID == 0) {
                    question.Position = questionCounter;
                    question.DisplayPosition = questionCounter.toString();
                    questionCounter++;
                    dependentCounter = 1;
                }
                else {
                    question.Position = questionCounter - 1;
                    question.DisplayPosition = `${questionCounter - 1}.${dependentCounter}`;
                    dependentCounter++;
                }

                for (let answer of question.Answers) {
                    if (!answer.Active)
                        continue;

                    answer.Position = answerCounter++;
                }

                answerCounter = 1;
            }

            questionCounter = 1;
            questionOrderCounter = 1;
            dependentCounter = 1;
        }
    }

    /**
     * Adds a standard answer to a question.
     * @param question Question to add the answer to
     * @param name Name of the answer to use
     */
    public addAnswer(question: Question, name?: string) {
        // If a Yes/No question had an N/A answer that was marked for deletion, just bring it back
        if (question.QuestionType == QuestionType.YesNo && this.questionAnswerCount(question) == 2 && question.Answers.length == 3)
            question.Answers[2].Active = true;
        else {
            let newAnswer: Answer = <Answer>{
                AnswerID: 0,
                QuestionID: question.QuestionID,
                AnswerName: name || "",
                PercentCorrect: 0,
                PercentCorrectDisplay: 0,
                Alert: false,
                Active: true,
                Position: this.questionAnswerCount(question) + 1,
                ZeroForm: false,
                ShowsDependents: false
            };
            question.Answers.push(newAnswer);
        }

        this.refreshPositions();
    }

    /**
     * Adds the standard layout of answers to a question depending on its type. Will do nothing if the question already has answers.
     * @param question Question to fill with its standard array of answers
     */
    public addDefaultAnswers(question: Question) {
        if (this.questionAnswerCount(question) > 0 || question.QuestionType == QuestionType.Text || question.QuestionType < 0)
            return;

        switch (question.QuestionType) {
            case QuestionType.VerticalRadioList:
            case QuestionType.DropDownList:
            case QuestionType.MultiSelect:
                this.addAnswer(question);
                this.addAnswer(question);
                this.addAnswer(question);
                break;
            case QuestionType.Checkbox:
                this.addAnswer(question);
                this.addAnswer(question, " (unchecked)");
                break;
            case QuestionType.YesNo:
                this.addAnswer(question, "Yes");
                this.addAnswer(question, "No");
                break;
        }
    }

    /**
     * Changes a question from one type to another, altering answers as needed.
     * @param question Question to change type of
     * @param newType Type to change the question to
     * @param questionIndex Index of question within its group; only needed when changing type to Text
     * @param group Group containing the question; only needed when changing type to Text
     */
    public changeQuestionType(question: Question, newType: QuestionType, questionIndex?: number, group?: QuestionGroup) {
        if (question.QuestionType == newType)
            return;

        let oldType = question.QuestionType;
        question.QuestionType = newType;

        this.confirmDialogTypeOpen = ConfirmationDialogType.None;

        if ((oldType == QuestionType.VerticalRadioList && newType == QuestionType.DropDownList) || (oldType == QuestionType.DropDownList && newType == QuestionType.VerticalRadioList))
            return;

        if (oldType == QuestionType.Checkbox && newType == QuestionType.YesNo) {
            question.Answers[0].AnswerName = "Yes";
            question.Answers[1].AnswerName = "No";
            return;
        }
        else if (oldType == QuestionType.YesNo && newType == QuestionType.Checkbox) {
            question.Answers[0].AnswerName = "";
            question.Answers[1].AnswerName = " (unchecked)";
            if (question.Answers.length == 3) // Remove N/A answer if it is there
                this.removeAnswer(2, question);
            return;
        }

        if (newType == QuestionType.Text)
            this.removeDependency(question, questionIndex, group); // Text types cannot have dependents

        // Incompatible question type, clear existing and add default
        for (let i = 0; i < question.Answers.length; i++) {
            this.removeAnswer(i, question);
        }
        this.addDefaultAnswers(question);
    }

    /**
     * Removes an answer from a question.
     * @param answerIndex Index of answer to remove
     * @param question Question to remove answer from
     */
    public removeAnswer(answerIndex: number, question: Question) {
        let answer = question.Answers[answerIndex];

        if (answer.AnswerID == 0)
            question.Answers.splice(answerIndex, 1);
        else
            answer.Active = false;

        this.refreshPositions();
        this.recalculateWeight(question);
    }

    /**
     * Opens the confirmation dialog with the correct settings.
     * @param dialogType Type of confirmation box to open
     * */
    public openConfirmationDialog(dialogType: ConfirmationDialogType, parameter1?, parameter2?, parameter3?, parameter4?) {
        this.confirmDialogTypeOpen = dialogType;
        this.confirmParam1 = parameter1;
        this.confirmParam2 = parameter2;
        this.confirmParam3 = parameter3;
        this.confirmParam4 = parameter4;
    }

    /**
     * Returns the properly-formatted form of the inputted question group's numbering, accounting for dependency
     * @param form Form that the question belongs to
     * @param questionGroup Question Group to get numbering of
     * */
    public groupNumber(form: ApplicationDTO, questionGroup: QuestionGroup) {
        let count = 0;

        for (let iGroup of form.QuestionGroups) {
            if (!iGroup.Active)
                continue;

            count++;

            if (iGroup.QuestionGroupID == questionGroup.QuestionGroupID)
                return count + ".";
        }

        return "0.";
    }

    /**
     * Returns the properly-formatted form of the inputted question's numbering
     * @param question Question to get the numbering of
     */
    public questionNumber(question: Question) {
        if (!question.DisplayPosition.includes("."))
            return `${question.DisplayPosition}.`;
        return question.DisplayPosition;
    }

    /**
     * Returns true if the question has at least one answer with an alert
     * @param question Question to search for alerting answers
     */
    public questionHasAlert(question: Question) {
        for (let answer of question.Answers) {
            if (answer.Alert)
                return true;
        }

        return false;
    }

    /**
     * Returns true if the question has at least one answer with a zero form
     * @param question Question to search for zero-forming answers
     */
    public questionHasZeroForm(question: Question) {
        for (let answer of question.Answers) {
            if (answer.ZeroForm)
                return true;
        }

        return false;
    }

    /**
     * Sets all answers' PercentCorrectDisplay property in the specified form to a string representation of the PercentCorrect property
     * @param form Form to perform operation on 
     */
    public populatePointDisplay(form: ApplicationDTO) {
        for (let group of form.QuestionGroups) {
            for (let question of group.Questions) {
                for (let answer of question.Answers) {
                    if (answer.PercentCorrect == null) {
                        answer.PercentCorrectDisplay = "N/A";
                        continue;
                    }
                    answer.PercentCorrectDisplay = answer.PercentCorrect.toString();
                }
            }
        }
    }

    /**
     * Changes the current survey's end date between indefinite and simply empty.
     * */
    public toggleSurveyIndefiniteness() {
        this.selectedFormEditing.Survey.EndDate = this.selectedFormEditing.Survey.EndDate === null ? undefined : null;
    }

    /**
     * Puts the specified date into the proper format.
     * @param date Date to format
     */
    public formattedDate(date) {
        if (date === null)
            return "Indefinitely";
        if (!date)
            return "N/A";
        return formatDate(date, this.cvAngularDateFormat, "en-US");
    }

    /**
     * Loads the extended data for a form (question groups, questions, answers, assigned groups, and survey information) and inserts it
     * @param form Form to pull information and insert into
     */
    public async sendSurveyNotification() {
        if (this.isDataSaving)
            return;

        try {
            this.isDataSaving = true;
            await this.formsService.sendSurveyNotification(this.selectedForm.ApplicationID);
        }
        catch (error) {
            console.log(error);
        }
        finally {
            this.isDataSaving = false;
        }
    }

    /**
     * Changes the specified answer's PercentCorrect property to match the recently-changed PercentCorrectDisplay property
     * @param answer Answer that was changed
     * @param question Question that the answer belongs to
     */
    public pointsPossibleChanged(answer, question: Question) {
        if (isNaN(parseInt(answer.PercentCorrectDisplay)) || parseInt(answer.PercentCorrectDisplay) < 0) {
            answer.PercentCorrect = null;
            answer.PercentCorrectDisplay = "N/A";
            this.recalculateWeight(question);
            return;
        }

        if (parseInt(answer.PercentCorrectDisplay) > 1000)
            answer.PercentCorrectDisplay = "1000";

        answer.PercentCorrect = parseInt(answer.PercentCorrectDisplay);

        this.recalculateWeight(question);
    }

    /**
     * Increases the specified answer's PercentCorrect and PercentCorrectDisplay properties by 1
     * @param answer Answer to increment
     * @param question Question that the answer belongs to
     */
    public decrementPoints(answer, question: Question) {
        if (answer.PercentCorrect == null)
            return;

        if (answer.PercentCorrect == 0) {
            answer.PercentCorrect = null;
            answer.PercentCorrectDisplay = "N/A";
            return;
        }

        answer.PercentCorrect--;
        answer.PercentCorrectDisplay = answer.PercentCorrect.toString();

        this.recalculateWeight(question);
    }

    /**
     * Decreases the specified answer's PercentCorrect and PercentCorrectDisplay properties by 1
     * @param answer Answer to decrement
     * @param question Question that the answer belongs to
     */
    public incrementPoints(answer, question: Question) {
        if (answer.PercentCorrect == 1000)
            return;

        if (answer.PercentCorrect == null) {
            answer.PercentCorrect = 0;
            answer.PercentCorrectDisplay = "0";
            return;
        }

        answer.PercentCorrect++;
        answer.PercentCorrectDisplay = answer.PercentCorrect.toString();

        this.recalculateWeight(question);
    }

    /**
     * Recalculates the weight of the question based on the value of its answers' PercentCorrect field.
     * @param question Question to recalculate weight of
     */
    public recalculateWeight(question: Question) {
        let totalWeight = 0;
        if (question.QuestionType != 6) {
            for (let answer of question.Answers) {
                if (answer.Active && (!!answer.PercentCorrect && answer.PercentCorrect > totalWeight))
                    totalWeight = answer.PercentCorrect;
            }

        } else {
            for (let answer of question.Answers) {
                if(answer.Active)
                    totalWeight += answer.PercentCorrect;
            }
        }
        question.QuestionWeight = totalWeight;
    }

    /**
     * Returns the list of the names of all answers that will trigger the current question, each separated by <br>
     * @param question Dependent question
     * @param group Group that the question belongs to
     */
    public dependentTriggeringAnswers(question: Question, group: QuestionGroup) {
        if (question.DependentID == 0)
            return;

        let answerNames = "";
        let parentQuestion = group.Questions.find(o => o.QuestionID == question.DependentID);

        for (let answer of parentQuestion.Answers) {
            if (answer.ShowsDependents)
                answerNames += answer.AnswerName + "<br>";
        }

        if (answerNames == "")
            return "(none)";

        return answerNames;
    }

    /**
     * Automatically changes the unchecked answer of a checkbox question to that of the first answer with "(unchecked)" added to the end.
     * Does nothing if invoked on a non-checkbox question.
     * @param question Question to manipulate
     * @param answerIndex Index of the answer that was changed
     */
    public answerNameChanged(question: Question, answerIndex: number) {
        if (question.QuestionType != QuestionType.Checkbox || answerIndex > 0)
            return;

        question.Answers[1].AnswerName = question.Answers[0].AnswerName + " (unchecked)";
    }

    /**
     * Returns true if a question in the print view should be shown, false if it should be hidden.
     * @param question Question to determine visibility of
     * @param group Group that the question is a part of
     * @param groupIndex Index of the group in the form
     */
    public showQuestionPrint(question: Question, group: QuestionGroup, groupIndex: number) {
        if (!question.Active)
            return false;
        if (question.DependentID == 0)
            return true;

        // Find parent question
        let parentQuestion;
        let parentQuestionIndex;
        for (let i = 0; i < group.Questions.length; i++) {
            if (group.Questions[i].QuestionID == question.DependentID) {
                parentQuestion = group.Questions[i];
                parentQuestionIndex = i;
                break;
            }
        }

        // Determine if parent question has dependent-triggering answer selected
        let selectedAnswerId = this.printAnswers[groupIndex][parentQuestionIndex];
        for (let answer of parentQuestion.Answers) {
            if (answer.AnswerID == selectedAnswerId)
                return answer.ShowsDependents;
        }

        // No answer selected
        return false;
    }

    /**
     * Toggles the value of a checkbox in the print view.
     * @param question Question the checkbox belongs to
     * @param questionIndex Index of the question in the group
     * @param groupIndex Index of the group in the form
     */
    public toggleCheckValue(question: Question, questionIndex: number, groupIndex: number) {
        if (this.printAnswers[groupIndex][questionIndex] == question.Answers[0].AnswerID)
            this.printAnswers[groupIndex][questionIndex] = question.Answers[1].AnswerID;
        else this.printAnswers[groupIndex][questionIndex] = question.Answers[0].AnswerID;
    }

    /**
     * Listens to scrolling and sets the Scroll to Top button to invisible if scrolled to the top and visible if scrolled below that.
     * @param event
     */
    @HostListener('window:scroll', ['$event'])
    public setScrollButtonVisibility(event) {
        this.showScrollButton = window.pageYOffset > 0;
    }

    /**
     * Scrolls the view to the top.
     * */
    public scrollToTop() {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
    }

    /**
     * Returns the number of rows currently selected by the grid. Useful to avoid certain errors.
     * @param grid Grid to get selected row count of
     */
    public selectedRowCount(grid: GridOptions) {
        if (!grid || !this.agGrid?.api)
            return 0;
        return this.agGrid?.api.getSelectedRows().length;
    }

    /**
     * Applies filter to the AG Grid.
     * */
    public applyFilter() {
        if (this.agGrid?.api)
            this.agGrid?.api.onFilterChanged();
    }

    /**
     * Ensures that the take limit input is in the correct format and not negative.
     * @returns
     */
    public cleanTakeLimitInput() {
        if (Number.isNaN(+this.selectedFormEditing.Survey.TakeLimitPerTimeFrame) || this.selectedFormEditing.Survey.TakeLimitPerTimeFrame < 0)
            this.selectedFormEditing.Survey.TakeLimitPerTimeFrame = 0;

        setTimeout(() => {
            this.cdr.detectChanges();
        }, 1);

        return this.selectedFormEditing.Survey.TakeLimitPerTimeFrame;
    }

    /**
     * Ensures that the start date is less than equal to the end date when check box is unchecked.
     * @returns
     */
    public checkUpdateStartDate() {
        if(this.selectedFormEditing.Survey.StartDate != null && this.editSurveyEndDate != null && this.selectedFormEditing.Survey.StartDate > this.editSurveyEndDate)
            this.selectedFormEditing.Survey.StartDate = this.editSurveyEndDate;
    }
}
